import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import useAbortController from './useAbortController';

export default (method, params = {}, options = {}) => {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const { newAbortSignal, cancelPreviousRequest, isCancel } =
    useAbortController();
  const cacheKey = JSON.stringify(params);
  const ref = useRef(
    useMemo(() => {
      return {
        cacheKey,
        isInitial: true,
      };
    }, []),
  );
  const { current: refData } = ref;
  const hasParamsChanged = refData.cacheKey !== cacheKey;
  const isInitial = refData.isInitial;
  const { skip } = options;

  ref.current.isInitial = false;
  if (hasParamsChanged) {
    ref.current.cacheKey = cacheKey;
  }

  useEffect(() => {
    if (options.skip) {
      setLoading(false);
    } else if (hasParamsChanged || (isInitial && !skip)) {
      request(params);
    }
  }, [hasParamsChanged, skip, params]);

  const request = useCallback(async (queryParams) => {
    try {
      setLoading(true);
      setError(null);
      cancelPreviousRequest();
      if (typeof queryParams === 'object') {
        queryParams.signal = newAbortSignal();
      }
      const result = Array.isArray(queryParams)
        ? await method.apply(null, queryParams)
        : await method(queryParams);
      if (options.onComplete) {
        options.onComplete(result);
      }
      setData(result);
      return result;
    } catch (e) {
      if (isCancel(e)) {
        return;
      }
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const resend = () => request(params);

  const updateCache = (callback) => {
    setData(callback(data));
  };

  return [
    data,
    {
      data,
      loading,
      error,
      updateCache,
      refetch: resend,
    },
  ];
};
